import type { ReactNode } from 'react';
import clsx from 'clsx';

/**
 * One line of a derivation. `formula` carries the arithmetic ("22 × $1,000"),
 * `value` the amount it produces. A line may have either, or both.
 */
export interface WorkingsLine {
  label: string;
  /** The arithmetic that produces the value, shown in mono next to the label. */
  formula?: string;
  /** The resulting amount, right-aligned. Pass through `aud()` so it masks. */
  value?: string;
  tone?: 'default' | 'muted' | 'total' | 'good' | 'bad';
  /** Nest under the line above — a component of a subtotal. */
  indent?: boolean;
  /** Rule above this line, marking where a subtotal is struck. */
  rule?: boolean;
}

const TONE: Record<NonNullable<WorkingsLine['tone']>, string> = {
  default: 'text-cream',
  muted: 'text-cream-soft',
  total: 'text-cream font-semibold',
  good: 'text-mint font-semibold',
  bad: 'text-blood font-semibold',
};

/**
 * A numeric derivation, rendered as a readable chain: label, the arithmetic that
 * produced it, and the amount. The "middle step" between a headline figure and
 * the records behind it, so a number can be checked by hand rather than trusted.
 *
 * Generalised from `SteeringExplainer`, which does the same job for the one
 * cash-vs-card rule. Presentation only — every figure is passed in already
 * computed (and already formatted through `aud()`, so persona masking carries
 * through). Never recompute here.
 */
export default function Workings({
  lines,
  note,
  check,
  testId,
  children,
}: {
  lines: WorkingsLine[];
  /** Plain-English caveat or definition under the chain. */
  note?: string;
  /** A conservation statement — the reason the reader can trust the chain. */
  check?: string;
  testId?: string;
  /** An extra block under the chain — e.g. a time-phased ladder, where the
   *  totals above are only half the story because the money is also due at
   *  particular moments. */
  children?: ReactNode;
}) {
  return (
    <div className="space-y-2" data-testid={testId}>
      <dl className="text-[13px]">
        {lines.map((line, i) => (
          <div
            key={`${line.label}-${i}`}
            className={clsx(
              'flex items-baseline justify-between gap-4 py-0.5',
              line.rule && 'border-t border-gold-dim/30 mt-1 pt-1.5',
            )}
          >
            <dt
              className={clsx(
                'flex items-baseline gap-2 min-w-0',
                line.indent && 'pl-4',
                line.tone === 'total' ? 'text-cream' : 'text-cream-soft',
              )}
            >
              <span className="truncate">{line.label}</span>
              {line.formula ? (
                <span className="font-mono text-[11.5px] text-cream-faint shrink-0">{line.formula}</span>
              ) : null}
            </dt>
            {line.value ? (
              <dd className={clsx('font-mono tabular-nums shrink-0', TONE[line.tone ?? 'default'])}>
                {line.value}
              </dd>
            ) : null}
          </div>
        ))}
      </dl>
      {children}
      {check ? (
        <p
          className="rounded-md border border-mint/40 bg-mint/5 px-2.5 py-1.5 font-mono text-[11.5px] text-mint"
          data-testid={testId ? `${testId}-check` : undefined}
        >
          ✓ {check}
        </p>
      ) : null}
      {note ? <p className="font-serif text-[12px] text-cream-soft">{note}</p> : null}
    </div>
  );
}
